import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../../../shared/components/form';
import { detectCreditCard } from '../../../shared/utils/paymentUtil';
import { reportError } from '../../../shared/sentry';

const CardDetailsForm = ({ form, shouldShowError }) => {
  const [expiryDisplay, setExpiryDisplay] = useState('');
  const [cardBrand, setCardBrand] = useState({ type: null, icon: null, error: null });
  const cardDetectionTimeoutRef = useRef(null);

  // Reset state when form is reset or component unmounts
  useEffect(() => {
    return () => {
      if (cardDetectionTimeoutRef.current) {
        clearTimeout(cardDetectionTimeoutRef.current);
        cardDetectionTimeoutRef.current = null;
      }
    };
  }, []);

  // Reset card brand when card number is cleared
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'cardNumber' && !value?.cardNumber) {
        setCardBrand({ type: null, icon: null, error: null });
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Handle card number change
  const handleCardNumberChange = useCallback((value) => {
    const digitsOnly = value.replace(/[^0-9]/g, '').slice(0, 19);
    form.setValue('cardNumber', digitsOnly);
    form.clearErrors('cardNumber');

    if (cardDetectionTimeoutRef.current) {
      clearTimeout(cardDetectionTimeoutRef.current);
    }

    cardDetectionTimeoutRef.current = setTimeout(() => {
      if (digitsOnly.length === 0) {
        setCardBrand({ type: null, icon: null, error: null });
      } else {
        try {
          const detected = detectCreditCard(digitsOnly);
          setCardBrand(detected || { type: null, icon: null, error: null });
        } catch (error) {
          reportError(error, 'CardDetailsForm detect card brand');
          setCardBrand({ type: null, icon: null, error: null });
        }
      }
    }, 100);
  }, [form]);

  // Handle expiry change
  const handleExpiryChange = useCallback((value) => {
    let formatted = value.replace(/[^0-9/]/g, '');
    if (formatted.length === 2 && expiryDisplay.length === 1) {
      formatted += '/';
    }
    if (formatted.length > 5) {
      formatted = formatted.slice(0, 5);
    }
    setExpiryDisplay(formatted);

    const [mm, yy] = formatted.split('/');
    const expMonth = mm || '';
    const expYear = yy && yy.length === 2 ? '20' + yy : (yy || '');

    form.setValue('expMonth', expMonth, { shouldDirty: true, shouldTouch: true });
    form.setValue('expYear', expYear, { shouldDirty: true, shouldTouch: true });
    form.clearErrors('expMonth');
    form.clearErrors('expYear');
  }, [form, expiryDisplay]);

  // Handle CVV change - only allow digits
  const handleCvvChange = useCallback((value) => {
    const digitsOnly = value.replace(/[^0-9]/g, '').slice(0, 4);
    form.setValue('cvv2', digitsOnly);
    form.clearErrors('cvv2');
  }, [form]);

  // Reset expiry display when form is reset
  useEffect(() => {
    const subscription = form.watch((value) => {
      if (!value?.expMonth && !value?.expYear) {
        setExpiryDisplay('');
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <div id="card-details-form" className="form-section">
      <h3>Card Details</h3>
      <div className="form-grid-2">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem className="form-field">
              <FormLabel className="form-label">First Name</FormLabel>
              <FormControl>
                <input
                  {...field}
                  data-ccbill="firstName"
                  type="text"
                  placeholder="John"
                  onChange={(e) => {
                    field.onChange(e);
                    // Update nameOnCard immediately with the new firstName value
                    const lastName = form.watch('lastName') || '';
                    const combinedName = `${e.target.value || ''} ${lastName}`.trim();
                    form.setValue('nameOnCard', combinedName, { shouldValidate: false });
                    if (combinedName) {
                      form.clearErrors('nameOnCard');
                    }
                  }}
                  className={`form-input ${shouldShowError('firstName') ? 'error' : ''}`}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="form-field">
              <FormLabel className="form-label">Last Name</FormLabel>
              <FormControl>
                <input
                  {...field}
                  data-ccbill="lastName"
                  type="text"
                  placeholder="Doe"
                  onChange={(e) => {
                    field.onChange(e);
                    // Update nameOnCard immediately with the new lastName value
                    const firstName = form.watch('firstName') || '';
                    const combinedName = `${firstName} ${e.target.value || ''}`.trim();
                    form.setValue('nameOnCard', combinedName, { shouldValidate: false });
                    if (combinedName) {
                      form.clearErrors('nameOnCard');
                    }
                  }}
                  className={`form-input ${shouldShowError('lastName') ? 'error' : ''}`}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="nameOnCard"
        render={({ field }) => (
          <input
            {...field}
            type="hidden"
            data-ccbill="nameOnCard"
            value={field.value || ''}
          />
        )}
      />
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem className="form-field">
            <FormLabel className="form-label">Email</FormLabel>
            <FormControl>
              <input
                {...field}
                data-ccbill="email"
                type="email"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                placeholder="john@example.com"
                className={`form-input ${shouldShowError('email') ? 'error' : ''}`}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="cardNumber"
        render={({ field }) => (
          <FormItem className="form-field">
            <FormLabel className="form-label">Card Number</FormLabel>
            <FormControl>
              <div className="card-input-container">
                <input
                  {...field}
                  data-ccbill="cardNumber"
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  value={field.value}
                  onChange={(e) => handleCardNumberChange(e.target.value)}
                  maxLength={19}
                  className={`form-input ${shouldShowError('cardNumber') ? 'error' : ''}`}
                />
                {cardBrand?.icon && cardBrand?.type && (
                  <div className="card-icon-wrapper">
                    <i
                      className={`card-icon ${cardBrand.icon} ${cardBrand.type ? `card-icon-${cardBrand.type.toLowerCase().replace(/ /g, '-')}` : ''}`}
                      title={cardBrand.type || ''}
                    />
                  </div>
                )}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="form-grid-2">
        <FormField
          control={form.control}
          name="expMonth"
          render={() => {
            const watchedValues = form.watch();
            const expYearOnlyMessage =
              form.formState.errors.expYear?.message &&
              !form.formState.errors.expMonth?.message
                ? String(form.formState.errors.expYear.message)
                : '';
            return (
              <FormItem className="form-field">
                <FormLabel className="form-label">Expiry</FormLabel>
                <FormControl>
                  <div>
                    <input
                      name="expiryDisplay"
                      type="text"
                      placeholder="MM/YY"
                      value={expiryDisplay}
                      onChange={(e) => handleExpiryChange(e.target.value)}
                      maxLength={5}
                      className={`form-input ${(shouldShowError('expMonth') || shouldShowError('expYear')) ? 'error' : ''}`}
                    />
                    <input
                      type="hidden"
                      name="expMonth"
                      data-ccbill="expMonth"
                      value={watchedValues.expMonth}
                    />
                    <input
                      type="hidden"
                      name="expYear"
                      data-ccbill="expYear"
                      value={watchedValues.expYear}
                    />
                  </div>
                </FormControl>
                <FormMessage />
                {expYearOnlyMessage ? (
                  <div className="text-sm" style={{ color: '#ef4444' }}>
                    {expYearOnlyMessage}
                  </div>
                ) : null}
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="cvv2"
          render={({ field }) => (
            <FormItem className="form-field">
              <FormLabel className="form-label">CVV</FormLabel>
              <FormControl>
                <input
                  {...field}
                  data-ccbill="cvv2"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="123"
                  maxLength={4}
                  value={field.value || ''}
                  onChange={(e) => handleCvvChange(e.target.value)}
                  onKeyPress={(e) => {
                    // Prevent non-digit characters from being entered
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className={`form-input ${shouldShowError('cvv2') ? 'error' : ''}`}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default CardDetailsForm;

